// The path a copter actually flies through a mission, split into flight phases,
// for drawing the 3D corridor.
//
// flownPath() is a port of backend/utils/deconfliction.js flownPath() — the path
// the server's corridor monitor checks the drone against — so the tube on the map
// is the corridor that is enforced. Keep the two in step (flightPath.test.js
// pins the same cases the backend produces).
//   climb at home (ground → TAKEOFF height; ArduPilot ignores TAKEOFF's position)
//   → waypoints → LAND: level approach, then straight down there
//   → RTL: level back over home, then straight down at home.

// Mirrors backend/utils/waypointTypes.js NON_LOCATION_TYPES.
export const NON_LOCATION_TYPES = new Set([
  'RTL', 'CHANGE_SPEED', 'SET_HOME', 'DELAY', 'GUIDED_ENABLE',
  'CONDITION_DELAY', 'CONDITION_YAW', 'CONDITION_DISTANCE', 'CONDITION_CHANGE_ALT',
  'CAMERA_TRIGGER', 'IMAGE_START_CAPTURE', 'IMAGE_STOP_CAPTURE',
  'VIDEO_START_CAPTURE', 'VIDEO_STOP_CAPTURE', 'SET_CAMERA_ZOOM', 'SET_CAMERA_FOCUS',
  'DO_GIMBAL_MANAGER_PITCHYAW', 'DO_SET_SERVO', 'DO_REPEAT_SERVO',
  'DO_SET_RELAY', 'DO_REPEAT_RELAY', 'DO_GRIPPER', 'DO_AUX_FUNCTION',
  'DO_ENGINE_CONTROL', 'DO_GUIDED_LIMITS', 'DO_MOUNT_CONTROL', 'DO_JUMP', 'DO_WINCH',
  'SET_ROI_NONE', 'SCRIPT_TIME',
]);

// Points the path adds to the mission: no waypoint marker is drawn for them.
export const ADDED_POINT_TYPES = new Set(['TAKEOFF_GROUND', 'LAND_APPROACH', 'RETURN', 'HOME_GROUND', 'PLACE_GROUND']);

const isCommand = (p) => !p || (p.type && NON_LOCATION_TYPES.has(p.type));
const num = (a, b) => Number(a !== undefined && a !== null ? a : b);

function located(points) {
  const out = [];
  (points || []).forEach((p) => {
    if (isCommand(p)) return;
    const lat = num(p.lat, p.latitude);
    const lon = num(p.lon, p.longitude);
    const alt = num(p.alt, p.altitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) return;
    out.push({ lat, lon, alt: Number.isFinite(alt) ? alt : 0, frame: Number(p.frame) });
  });
  return out;
}

// RTL: a level leg back over home at the last height (backend withReturnLeg).
function withReturnLeg(points) {
  const rtlAt = points.findIndex((p) => p && p.type === 'RTL');
  if (rtlAt < 0) return points;
  const flown = located(points.slice(0, rtlAt));
  if (flown.length === 0) return points;
  const takeoff = located(points.filter((p) => p && p.type === 'TAKEOFF'))[0];
  const home = takeoff || flown[0];
  const last = flown[flown.length - 1];
  return [...points, {
    type: 'RETURN', frame: last.frame,
    lat: home.lat, lon: home.lon, alt: last.alt,
    latitude: home.lat, longitude: home.lon, altitude: last.alt,
  }];
}

// TAKEOFF happens where the drone stands, not where it was clicked.
function placeTakeoffAtLaunch(points, home) {
  const lat = Number(home && home.lat);
  const lon = Number(home && home.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) return points;
  return points.map((p) => (p && p.type === 'TAKEOFF' ? { ...p, lat, lon, latitude: lat, longitude: lon } : p));
}

export function flownPath(points, home) {
  if (!Array.isArray(points)) return [];
  const launched = placeTakeoffAtLaunch(points, home);
  const out = [];
  let prev = null;
  for (const p of launched) {
    if (isCommand(p)) { out.push(p); continue; }
    if (p.type === 'TAKEOFF') out.push({ ...p, type: 'TAKEOFF_GROUND', frame: 3, alt: 0, altitude: 0 });
    if (p.type === 'LAND' && prev) out.push({ ...p, type: 'LAND_APPROACH', frame: prev.frame, alt: prev.alt, altitude: prev.alt });
    out.push(p);
    const alt = num(p.alt, p.altitude);
    // Touch-drop: down to the ground (at most param1 m) and back — backend
    // deconfliction.js flownPath does the same, so the corridor is enforced as drawn.
    const placeDepth = Number(p.param1);
    if ((p.type === 'PAYLOAD_PLACE' || Number(p.command) === 94) && Number.isFinite(alt) && placeDepth > 0) {
      out.push({ ...p, type: 'PLACE_GROUND', alt: alt - placeDepth, altitude: alt - placeDepth });
    }
    if (Number.isFinite(alt)) prev = { alt, frame: p.frame };
  }
  const back = withReturnLeg(out);
  if (back === out) return out;
  const ret = back[back.length - 1];
  return [...back, { ...ret, type: 'HOME_GROUND', frame: 3, alt: 0, altitude: 0 }];
}

// Flight phase of each leg of a drawn path (points with lat, lon, alt, type):
//   takeoff — going up in place before the first leg that goes anywhere;
//   land    — going down in place after the last one (LAND, or home after RTL);
//   drop    — any other column in place (a delivery's descent and climb back);
//   cruise  — every leg that goes somewhere, level or sloped.
// Decided per leg, so a long leg is one colour end to end.
const VERTICAL_M = 1;
const horizontalM = (a, b) => Math.hypot(
  (b.lat - a.lat) * 111320,
  (b.lon - a.lon) * 111320 * Math.cos((a.lat * Math.PI) / 180),
);

export function legPhases(pts) {
  const vertical = [];
  for (let i = 0; i < pts.length - 1; i++) vertical.push(horizontalM(pts[i], pts[i + 1]) < VERTICAL_M);
  const firstCruise = vertical.indexOf(false);
  const lastCruise = vertical.lastIndexOf(false);
  return vertical.map((isVertical, i) => {
    if (!isVertical) return 'cruise';
    const up = Number(pts[i + 1].alt) >= Number(pts[i].alt);
    if (up && (firstCruise < 0 || i < firstCruise)) return 'takeoff';
    if (!up && (lastCruise < 0 || i > lastCruise)) return 'land';
    return 'drop';
  });
}

const HOME_MAX_DISTANCE_M = 10000;

// The flown path as [lat, lon] pairs, for a flat map: from where the copter
// stands, back there after RTL. A home implausibly far from the mission (stale or
// wrong) is ignored, so it cannot draw a line across the whole map.
export function drawnRoute(waypoints, home) {
  const items = (waypoints || []).filter((wp) => wp && wp.type !== 'HOME');
  const first = located(items.filter((wp) => wp.type !== 'TAKEOFF'))[0] || located(items)[0];
  const lat = Number(home && home.lat);
  const lon = Number(home && home.lon);
  const usable = !!first && Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0)
    && horizontalM({ lat, lon }, first) < HOME_MAX_DISTANCE_M;
  const route = [];
  located(flownPath(items, usable ? { lat, lon } : null)).forEach((p) => {
    const last = route[route.length - 1];
    if (!last || Math.abs(last[0] - p.lat) > 1e-7 || Math.abs(last[1] - p.lon) > 1e-7) route.push([p.lat, p.lon]);
  });
  return route;
}
